import React, { useCallback, useEffect, useMemo, useState } from "react";
import RenderedHtmlContent from "../rendered_html_content";
import axios from "axios";
import useTranslation from "../../effects/use_translation";
import { renderToString } from "react-dom/server";
import { attributesToProps, domToReact } from "html-react-parser";
import { FormProvider, useForm, useFormContext } from "react-hook-form";

/***
 EXAMPLE:

 <div role="tag-owner-search" serach-url="/user/networkings.json">
 <select role="additional_search_value" name="industry" class="form-select form-select-sm rounded-0 border-0">
 <option selected>Industry</option>
 <option value="Industry 1">Industry 1</option>
 <option value="Industry 2">Industry 2</option>
 <option value="Industry 3">Industry 3</option>
 </select>
 <div class="input-group mb-3">
 <input role="search-box" type="text" class="form-control" />
 <span className="input-group-text" role="only-while-loading">
 <i className="fa fa-spinner fa-spin"></i>
 </span>
 </div>
 <ul role="result-list" class="list-group list-group-flush"  placeholder="Test">
 <li class="list-group-item">
 {{tag_owner.name}}
 <hr/>
 {{tag_owner.email}}
 </li>
 </ul>
 </div>

 ***/
export const TagOwnerSearch = ({ node, options }) => {
  const translation = useTranslation();
  const methods = useForm();
  const additionalSearchValues = methods.watch();

  const [searchText, setSearchText] = useState("");
  const [tagOwners, setTagOwners] = useState([]);
  const [loading, setLoading] = useState([]);

  const searchUrl = node.attribs["search-url"];

  console.log("additionalSearchValues", { additionalSearchValues });

  const handleSearchTextChange = useCallback(
    (e) => {
      setSearchText(e.target.value);
    },
    [setSearchText]
  );

  useEffect(() => {
    if (!searchUrl) {
      alert("No search url");
      return;
    }

    setLoading(true);

    const loadingTimeout = setTimeout(() => {
      axios
        .post(searchUrl, {
          query: searchText,
          additional_search_values: additionalSearchValues,
        })
        .then((response) => {
          if (response.data.tag_owners) {
            setTagOwners(response.data.tag_owners);
          } else {
            setTagOwners([]);
          }
        })
        .catch((error) => {
          alert(error);
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 1500);

    return () => {
      clearTimeout(loadingTimeout);
    };
  }, [
    searchText,
    searchUrl,
    setSearchText,
    JSON.stringify(additionalSearchValues),
  ]);

  const TagOwnerSearchTag = node.name;
  const attrProps = attributesToProps(node.attribs);

  return (
    <FormProvider {...methods}>
      <TagOwnerSearchTag {...attrProps}>
        {node.children.map((child) => (
          <MappedChild
            node={child}
            loading={loading}
            tagOwners={tagOwners}
            options={options}
            value={searchText}
            onChange={handleSearchTextChange}
          />
        ))}
      </TagOwnerSearchTag>
    </FormProvider>
  );
};

const MappedChild = (props) => {
  const { node, tagOwners, options, value, onChange, loading } = props;
  const { reset } = useFormContext(); // retrieve all hook methods

  const TagType = node.name;
  const attrProps = attributesToProps(node.attribs);
  if (node.attribs?.role === "only-while-loading" && !loading) {
    return null;
  }

  const clearClickEvent = useMemo(() => {
    if (node.attribs?.role === "clear-additional-inputs") {
      return () => {
        reset();
      };
    } else {
      return undefined;
    }
  }, [reset, node.attribs?.role]);

  if (node.type === "text") {
    return <>{node.data}</>;
  } else if (node.attribs?.role === "search-box") {
    return (
      <SearchBox
        node={node}
        tagOwners={tagOwners}
        options={options}
        value={value}
        onChange={onChange}
      />
    );
  } else if (node.attribs?.role === "result-list") {
    return <ResultList node={node} tagOwners={tagOwners} options={options} />;
  } else if (node.attribs?.role === "additional-search-value") {
    return (
      <AdditionalSearchInput
        node={node}
        tagOwners={tagOwners}
        options={options}
      />
    );
  } else if (
    node.attribs?.role === "no-results-content" &&
    (loading || (tagOwners && tagOwners.length > 0))
  ) {
    return null;
  } else if (node.children && node.children.length > 0) {
    return (
      <TagType
        value={node.name === "option" ? node.attribs.value : undefined}
        onClick={clearClickEvent}
        {...attrProps}
      >
        {node.children.map((child) => (
          <MappedChild {...props} node={child} />
        ))}
      </TagType>
    );
  } else {
    return (
      <TagType
        value={node.name === "option" ? node.attribs.value : undefined}
        onClick={clearClickEvent}
        {...attrProps}
      ></TagType>
    );
  }
};

const SearchBox = ({ node, options, value, onChange }) => {
  const SearchBoxTag = node.name;

  const props = attributesToProps(node.attribs);

  return <SearchBoxTag {...props} value={value} onChange={onChange} />;
};

const ResultList = ({ node, options, tagOwners }) => {
  const ListContainerTag = node.name;

  const props = attributesToProps(node.attribs);

  const childString = useMemo(() => {
    return renderToString(<>{domToReact(node.children, options)}</>);
  }, [node, node.children, options]);

  return (
    <ListContainerTag {...props}>
      {tagOwners.map((tagOwner) => (
        <RenderedHtmlContent
          html={childString}
          placeholders={{ tagOwner, tag_owner: tagOwner }}
        />
      ))}
    </ListContainerTag>
  );
};

const AdditionalSearchInput = ({ node, options, tagOwners }) => {
  const ListContainerTag = node.name;
  const { register, trigger, setValue } = useFormContext(); // retrieve all hook methods

  const props = attributesToProps(node.attribs);

  const formProps = register(props["name"]);

  return (
    <ListContainerTag {...props} {...formProps}>
      {node.children && node.children.length > 0 ? (
        <>
          {node.children.map((child) => (
            <MappedChild node={child} />
          ))}
        </>
      ) : null}
    </ListContainerTag>
  );
};
